import React, { useEffect, useState, useMemo } from 'react';
import './Integrations.css';
import Spinner from './Spinner';
import axios from 'axios';
import DataTable, { DataTableColumn, ActionMenu, DataTableAction } from './DataTable';
import { Icons } from './ActionIcons';

type SapClient = {
  _id?: string;
  name: string;
  description?: string;
  host: string;
  port: number;
  client: string;
  username: string;
  password?: string;
  language?: string;
  useHttps: boolean;
  connectionType: 'odata' | 'http' | 'rfc';
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
};

type TestResult = {
  success: boolean;
  message: string;
  responseTime?: number;
  error?: string;
};

const Integrations: React.FC = () => {
  const [theme] = useState(localStorage.getItem('phoenixTheme') || 'light');
  const [clients, setClients] = useState<SapClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SapClient | null>(null);
  const [creating, setCreating] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<{ [key: string]: string }>({});
  const [showModal, setShowModal] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testPassword, setTestPassword] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [clientToTest, setClientToTest] = useState<SapClient | null>(null);
  const [readingTable, setReadingTable] = useState(false);
  const [tableData, setTableData] = useState<any[] | null>(null);
  const [callingBapi, setCallingBapi] = useState(false);
  const [bapiData, setBapiData] = useState<any>(null);

  // Toast notifications
  const [toast, setToast] = useState<{ visible: boolean; message: string; variant?: 'info' | 'danger' | 'success' }>({ visible: false, message: '', variant: 'info' });
  
  // Confirm dialog
  const [confirmState, setConfirmState] = useState<{ visible: boolean; title?: string; message?: string; onConfirm?: () => void }>({ visible: false });

  useEffect(() => { document.documentElement.setAttribute('data-bs-theme', theme); }, [theme]);

  // Resetar página quando filtro mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [columnFilters]);

  // Filtered clients
  const filteredClients = useMemo(() => {
    let list = clients.slice();
    
    // Filtros por coluna
    Object.entries(columnFilters).forEach(([columnKey, filterValue]) => {
      if (filterValue.trim()) {
        const filterLower = filterValue.trim().toLowerCase();
        list = list.filter(c => {
          let cellValue = '';
          
          switch(columnKey) {
            case 'name':
              cellValue = (c.name || '').toLowerCase();
              break;
            case 'description':
              cellValue = (c.description || '').toLowerCase();
              break;
            case 'host':
              cellValue = `${c.host}:${c.port}`.toLowerCase();
              break;
            case 'client':
              cellValue = (c.client || '').toLowerCase();
              break;
            case 'username':
              cellValue = (c.username || '').toLowerCase();
              break;
            case 'connectionType':
              cellValue = (c.connectionType || '').toLowerCase();
              break;
            case 'isActive':
              cellValue = c.isActive ? 'ativo' : 'inativo';
              break;
            default:
              cellValue = '';
          }
          
          return cellValue.includes(filterLower);
        });
      }
    });
    
    // Ordenação
    if (sortColumn) {
      list.sort((a: any, b: any) => {
        const getVal = (row: any, c: string) => {
          if (c === 'host') return `${row.host}:${row.port}`;
          if (c === 'isActive') return row.isActive ? 1 : 0;
          return row[c] || '';
        };
        const va = getVal(a, sortColumn);
        const vb = getVal(b, sortColumn);
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return list;
  }, [clients, sortColumn, sortDir, columnFilters]);

  // Dados paginados
  const paginatedClients = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredClients.slice(startIndex, endIndex);
  }, [filteredClients, currentPage, pageSize]);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = () => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash ? `${baseNoSlash}/api/sap-clients` : '/api/sap-clients';
    setLoading(true);
    axios.get(url, { withCredentials: true })
      .then(res => {
        setClients(Array.isArray(res.data) ? res.data : []);
        setError(null);
      })
      .catch(err => {
        setError(err?.response?.data?.message || err.message || 'Erro ao carregar clientes SAP');
        setClients([]);
      })
      .finally(() => setLoading(false));
  };

  const openCreate = () => {
    setEditing({
      name: '',
      description: '',
      host: '',
      port: 8000,
      client: '',
      username: '',
      password: '',
      language: 'PT',
      useHttps: true,
      connectionType: 'http',
      isActive: true
    });
    setCreating(true);
    setShowModal(true);
    setTestResult(null);
    setTestPassword('');
  };

  const openEdit = (c: SapClient) => {
    setEditing({ ...c, password: '' }); // Não carrega senha por segurança
    setCreating(false);
    setShowModal(true);
    setTestResult(null);
    setTestPassword('');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setTestResult(null);
    setTestPassword('');
  };

  const saveClient = async () => {
    if (!editing) return;
    if (!editing.name || !editing.name.trim() || !editing.host || !editing.client || !editing.username) {
      showToast('Preencha todos os campos obrigatórios (nome, host, mandante, usuário)', 'danger');
      return;
    }

    if (creating && !editing.password) {
      showToast('Senha é obrigatória ao criar novo cliente', 'danger');
      return;
    }

    try {
      const base = process.env.REACT_APP_API_URL || '';
      const baseNoSlash = base ? base.replace(/\/$/, '') : '';
      
      if (creating) {
        const url = baseNoSlash ? `${baseNoSlash}/api/sap-clients` : '/api/sap-clients';
        await axios.post(url, editing, { withCredentials: true });
        showToast('Cliente SAP criado com sucesso', 'success');
      } else {
        const url = baseNoSlash ? `${baseNoSlash}/api/sap-clients/${editing._id}` : `/api/sap-clients/${editing._id}`;
        await axios.put(url, editing, { withCredentials: true });
        showToast('Cliente SAP atualizado com sucesso', 'success');
      }
      closeModal();
      loadClients();
    } catch (err: any) {
      showToast(err?.response?.data?.message || err.message || 'Erro ao salvar cliente SAP', 'danger');
    }
  };

  const deleteClient = (c: SapClient) => {
    showConfirm(
      'Excluir Cliente SAP',
      `Tem certeza que deseja excluir o cliente "${c.name}"? Esta ação não pode ser desfeita.`,
      async () => {
        try {
          const base = process.env.REACT_APP_API_URL || '';
          const baseNoSlash = base ? base.replace(/\/$/, '') : '';
          const url = baseNoSlash ? `${baseNoSlash}/api/sap-clients/${c._id}` : `/api/sap-clients/${c._id}`;
          await axios.delete(url, { withCredentials: true });
          showToast('Cliente SAP excluído com sucesso', 'success');
          loadClients();
        } catch (err: any) {
          showToast(err?.response?.data?.message || err.message || 'Erro ao excluir cliente SAP', 'danger');
        }
      }
    );
  };

  const openTestModal = (client: SapClient) => {
    setClientToTest(client);
    setShowTestModal(true);
    setTestPassword('');
    setTestResult(null);
  };

  const closeTestModal = () => {
    setShowTestModal(false);
    setClientToTest(null);
    setTestPassword('');
    setTestResult(null);
    setTableData(null);
    setBapiData(null);
  };

  const testConnection = async () => {
    const client = editing || clientToTest;
    if (!client) return;

    setTesting(true);
    setTestResult(null);

    try {
      const base = process.env.REACT_APP_API_URL || '';
      const baseNoSlash = base ? base.replace(/\/$/, '') : '';
      const url = baseNoSlash ? `${baseNoSlash}/api/sap-clients/${client._id}/test-connection` : `/api/sap-clients/${client._id}/test-connection`;

      const res = await axios.post(url, {}, { withCredentials: true });
      
      setTestResult(res.data);
      showToast(res.data.message, res.data.success ? 'success' : 'danger');
    } catch (err: any) {
      const errorResult: TestResult = {
        success: false,
        message: err?.response?.data?.message || err.message || 'Erro ao testar conexão',
        error: err?.response?.data?.error || 'Erro desconhecido'
      };
      setTestResult(errorResult);
      showToast(errorResult.message, 'danger');
    } finally {
      setTesting(false);
    }
  };

  const callBapi = async () => {
    if (!clientToTest) {
      showToast('Execute o teste de conexão primeiro', 'danger');
      return;
    }

    setCallingBapi(true);
    setBapiData(null);

    try {
      const base = process.env.REACT_APP_API_URL || '';
      const baseNoSlash = base ? base.replace(/\/$/, '') : '';
      const url = baseNoSlash 
        ? `${baseNoSlash}/api/sap-clients/${clientToTest._id}/call-bapi` 
        : `/api/sap-clients/${clientToTest._id}/call-bapi`;

      const res = await axios.post(url, { 
        bapiName: 'BAPI_USER_GETLIST',
        parameters: {
          MAX_ROWS: 100
        }
      }, { withCredentials: true });
      
      if (res.data.success) {
        setBapiData(res.data.data[0]); // BAPI retorna objeto único
        showToast(`✅ BAPI BAPI_USER_GETLIST executada com sucesso!`, 'success');
      } else {
        showToast(res.data.error || 'Erro ao chamar BAPI', 'danger');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.message || err.message || 'Erro ao chamar BAPI', 'danger');
    } finally {
      setCallingBapi(false);
    }
  };

  const readTable = async () => {
    if (!clientToTest) {
      showToast('Execute o teste de conexão primeiro', 'danger');
      return;
    }

    setReadingTable(true);
    setTableData(null);

    try {
      const base = process.env.REACT_APP_API_URL || '';
      const baseNoSlash = base ? base.replace(/\/$/, '') : '';
      const url = baseNoSlash 
        ? `${baseNoSlash}/api/sap-clients/${clientToTest._id}/read-table` 
        : `/api/sap-clients/${clientToTest._id}/read-table`;

      const res = await axios.post(url, { 
        tableName: 'AGR_USERS',
        fields: [], // Todos os campos
        maxRows: 100,
        where: ''
      }, { withCredentials: true });
      
      if (res.data.success) {
        setTableData(res.data.data);
        showToast(`✅ ${res.data.rowCount} registros carregados da tabela AGR_USERS`, 'success');
      } else {
        showToast(res.data.error || 'Erro ao ler tabela', 'danger');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.message || err.message || 'Erro ao ler tabela', 'danger');
    } finally {
      setReadingTable(false);
    }
  };

  const showToast = (message: string, variant: 'info' | 'danger' | 'success' = 'info') => {
    setToast({ visible: true, message, variant });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 4000);
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
    setConfirmState({ visible: true, title, message, onConfirm });
  };

  const hideConfirm = () => setConfirmState({ visible: false });

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
  };

  const handleColumnFilter = (columnKey: string, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
  };

  // Função para gerar iniciais do nome
  const getInitials = (name: string | undefined): string => {
    if (!name) return '?';
    const words = name.trim().split(' ').filter(n => n.length > 0);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  // Função para gerar cor do avatar baseada no nome
  const getAvatarColor = (name: string | undefined): string => {
    if (!name) return '#6c757d';
    const colors = [
      '#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545',
      '#fd7e14', '#ffc107', '#198754', '#20c997', '#0dcaf0'
    ];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // Define table columns
  const columns: DataTableColumn<SapClient>[] = [
    {
      key: 'name',
      label: 'Nome',
      sortable: true,
      render: (value: any, row: SapClient) => (
        <div className="user-name-cell">
          <div 
            className="user-avatar" 
            style={{ backgroundColor: getAvatarColor(value) }}
            title={value || ''}
          >
            {getInitials(value)}
          </div>
          <span>{value || ''}</span>
        </div>
      )
    },
    {
      key: 'host',
      label: 'Host',
      sortable: true,
      render: (_: any, row: SapClient) => `${row.host}:${row.port}`
    },
    {
      key: 'client',
      label: 'Mandante',
      sortable: true
    },
    {
      key: 'username',
      label: 'Usuário',
      sortable: true
    },
    {
      key: 'connectionType',
      label: 'Tipo',
      sortable: true,
      render: (value: any, row: SapClient) => (
        <>
          <span className="text-uppercase">{value}</span>
          {' '}
          <small className="text-muted">({row.useHttps ? 'HTTPS' : 'HTTP'})</small>
        </>
      )
    },
    {
      key: 'isActive',
      label: 'Status',
      sortable: true,
      render: (value: any) => (
        <span className={`badge ${value ? 'bg-success' : 'bg-secondary'}`}>
          {value ? 'Ativo' : 'Inativo'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Ações',
      sortable: false,
      render: (_: any, client: SapClient) => {
        const actions: DataTableAction[] = [
          {
            key: 'test',
            label: 'Testar conexão',
            icon: (
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.251.068a.5.5 0 0 1 .227.58L9.677 6.5H13a.5.5 0 0 1 .364.843l-8 8.5a.5.5 0 0 1-.842-.49L6.323 9.5H3a.5.5 0 0 1-.364-.843l8-8.5a.5.5 0 0 1 .615-.09z"/>
              </svg>
            ),
            onClick: () => openTestModal(client),
            variant: 'success'
          },
          {
            key: 'edit',
            label: 'Editar',
            icon: Icons.Pencil,
            onClick: () => openEdit(client),
            variant: 'primary'
          },
          {
            key: 'delete',
            label: 'Excluir',
            icon: Icons.Trash,
            onClick: () => deleteClient(client),
            variant: 'danger'
          }
        ];
        
        return <ActionMenu actions={actions} />;
      }
    }
  ];

  if (loading) {
    return (
      <>
        
        <Spinner />
      </>
    );
  }

  return (
    <>
      <div className="container py-5" style={{ marginTop: 80 }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h1 className="mb-0">Integrações SAP</h1>
            <p className="lead mb-0">Configure conexões com ambientes SAP (ECC/S4HANA)</p>
          </div>
          <div>
            <button 
              className="btn btn-primary" 
              onClick={openCreate}
              style={{ padding: '0.5rem 1.25rem', fontWeight: 600 }}
            >
              Novo Cliente SAP
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <DataTable
          columns={columns}
          data={paginatedClients}
          keyExtractor={(client: SapClient) => client._id || client.name}
          sortColumn={sortColumn}
          sortDirection={sortDir}
          onSort={handleSort}
          onFilter={handleColumnFilter}
          filters={columnFilters}
          striped
          hover
          responsive
          emptyMessage="Nenhum cliente SAP configurado"
          pagination={true}
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={filteredClients.length}
          onPageChange={(page) => setCurrentPage(page)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[5, 10, 25, 50]}
        />

        <div className="integration-footer">
          <p className="text-muted mb-0">
            {filteredClients.length} {filteredClients.length === 1 ? 'cliente' : 'clientes'}
          </p>
        </div>
      </div>

      {/* Modal de Criar/Editar */}
      {showModal && editing && (
        <div className="integration-modal-overlay" onClick={closeModal}>
          <div className="integration-modal" onClick={e => e.stopPropagation()}>
            <div className="integration-modal-header">
              <h3>{creating ? 'Novo Cliente SAP' : 'Editar Cliente SAP'}</h3>
              <button className="integration-modal-close" onClick={closeModal} aria-label="Fechar">
                ×
              </button>
            </div>
            <div className="integration-modal-body">
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label">Nome <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={editing.name}
                    onChange={e => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Ex: SAP Produção"
                    autoFocus
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Mandante <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength={3}
                    value={editing.client}
                    onChange={e => setEditing({ ...editing, client: e.target.value })}
                    placeholder="100"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Descrição</label>
                  <textarea
                    className="form-control"
                    value={editing.description || ''}
                    onChange={e => setEditing({ ...editing, description: e.target.value })}
                    placeholder="Descrição do cliente SAP (opcional)"
                    rows={2}
                  />
                </div>
                <div className="col-md-8">
                  <label className="form-label">Host/IP <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={editing.host}
                    onChange={e => setEditing({ ...editing, host: e.target.value })}
                    placeholder="sap.empresa.com.br"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    {editing.connectionType === 'rfc' ? 'Instância SAP' : 'Porta'}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editing.port}
                    onChange={e => setEditing({ ...editing, port: e.target.value as any })}
                    placeholder={editing.connectionType === 'rfc' ? '00' : '8000'}
                  />
                  {editing.connectionType === 'rfc' ? (
                    <small className="text-muted">Ex: 00, 01, 02 (número da instância)</small>
                  ) : (
                    <small className="text-muted">Ex: 8000, 443, 44300</small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Usuário <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={editing.username}
                    onChange={e => setEditing({ ...editing, username: e.target.value })}
                    placeholder="TRUSTSIS_USER"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Senha {creating ? <span className="text-danger">*</span> : '(deixe vazio para manter)'}
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    value={editing.password || ''}
                    onChange={e => setEditing({ ...editing, password: e.target.value })}
                    placeholder={creating ? 'Senha SAP' : 'Nova senha (opcional)'}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Idioma</label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength={2}
                    value={editing.language}
                    onChange={e => setEditing({ ...editing, language: e.target.value?.toUpperCase() })}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Tipo de Conexão</label>
                  <select
                    className="form-select"
                    value={editing.connectionType}
                    onChange={e => {
                      const newType = e.target.value as any;
                      setEditing({ 
                        ...editing, 
                        connectionType: newType,
                        // RFC não usa HTTPS, então desmarca automaticamente
                        useHttps: newType === 'rfc' ? false : editing.useHttps
                      });
                    }}
                  >
                    <option value="http">HTTP</option>
                    <option value="odata">OData</option>
                    <option value="rfc">RFC (Recomendado)</option>
                  </select>
                  <small className="d-block text-muted mt-1">
                    {editing.connectionType === 'rfc' && '💡 RFC: Conexão direta, mais rápida e confiável'}
                    {editing.connectionType === 'http' && '💡 HTTP: Endpoints REST customizados'}
                    {editing.connectionType === 'odata' && '💡 OData: Serviços OData do SAP'}
                  </small>
                </div>
                <div className="col-md-4">
                  <label className="form-label d-block">Opções</label>
                  {editing.connectionType !== 'rfc' && (
                    <div className="form-check form-check-inline">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={editing.useHttps}
                        onChange={e => setEditing({ ...editing, useHttps: e.target.checked })}
                        id="useHttps"
                      />
                      <label className="form-check-label" htmlFor="useHttps">HTTPS</label>
                    </div>
                  )}
                  {editing.connectionType === 'rfc' && (
                    <small className="text-muted d-block">RFC não usa HTTP/HTTPS</small>
                  )}
                  <div className="form-check form-check-inline">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={editing.isActive}
                      onChange={e => setEditing({ ...editing, isActive: e.target.checked })}
                      id="isActive"
                    />
                    <label className="form-check-label" htmlFor="isActive">Ativo</label>
                  </div>
                </div>

                {/* Teste de conexão (apenas em edição) */}
                {!creating && (
                  <>
                    <div className="col-12"><hr /></div>
                    <div className="col-12">
                      <h6 className="mb-3">Testar Conexão</h6>
                    </div>
                    <div className="col-md-8">
                      <input
                        type="password"
                        className="form-control"
                        value={testPassword}
                        onChange={e => setTestPassword(e.target.value)}
                        placeholder="Digite a senha para testar"
                      />
                    </div>
                    <div className="col-md-4">
                      <button
                        className="btn btn-outline-success w-100"
                        onClick={testConnection}
                        disabled={testing || !testPassword}
                      >
                        {testing ? 'Testando...' : 'Testar'}
                      </button>
                    </div>
                    {testResult && (
                      <div className="col-12">
                        <div className={`alert alert-${testResult.success ? 'success' : 'danger'} mb-0`}>
                          <strong>{testResult.message}</strong>
                          {testResult.responseTime && (
                            <small className="d-block">Tempo: {testResult.responseTime}ms</small>
                          )}
                          {testResult.error && (
                            <small className="d-block">Erro: {testResult.error}</small>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="integration-modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={saveClient}>
                {creating ? 'Criar' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Teste Rápido */}
      {showTestModal && clientToTest && (
        <div className="integration-modal-overlay" onClick={closeTestModal}>
          <div className="integration-modal integration-modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="integration-modal-header">
              <h3>Testar Conexão - {clientToTest.name}</h3>
              <button className="integration-modal-close" onClick={closeTestModal} aria-label="Fechar">
                ×
              </button>
            </div>
            <div className="integration-modal-body">
              <div className="mb-3">
                <p className="text-muted mb-2">
                  <strong>Host:</strong> {clientToTest.host}:{clientToTest.port}<br />
                  <strong>Mandante:</strong> {clientToTest.client}<br />
                  <strong>Usuário:</strong> {clientToTest.username}
                </p>
                <p className="text-info-emphasis">
                  <small>🔒 A senha criptografada será usada automaticamente para testar a conexão.</small>
                </p>
              </div>
              {testResult && (
                <div className={`alert alert-${testResult.success ? 'success' : 'danger'} mb-0`}>
                  <div className="d-flex align-items-start">
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 16 16" 
                      fill="currentColor" 
                      xmlns="http://www.w3.org/2000/svg"
                      className="me-2 flex-shrink-0"
                      style={{ marginTop: '2px' }}
                    >
                      {testResult.success ? (
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                      ) : (
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                      )}
                    </svg>
                    <div className="flex-grow-1">
                      <strong>{testResult.message}</strong>
                      {testResult.responseTime && (
                        <small className="d-block mt-1">
                          ⏱️ Tempo de resposta: <strong>{testResult.responseTime}ms</strong>
                        </small>
                      )}
                      {testResult.error && (
                        <small className="d-block mt-1">
                          ⚠️ Detalhes: {testResult.error}
                        </small>
                      )}
                      {!testResult.success && testResult.error?.includes('SSL') && (
                        <div className="mt-2 pt-2 border-top">
                          <strong className="d-block mb-1">💡 Como resolver:</strong>
                          <small className="d-block">
                            • Edite o cliente e <strong>desmarque "HTTPS"</strong> para usar HTTP<br/>
                            • Ou troque para uma porta HTTPS (443, 44300)<br/>
                            • Porta {clientToTest.port} geralmente usa {clientToTest.useHttps ? 'HTTP' : 'HTTPS'}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Exibição dos dados da tabela */}
              {tableData && tableData.length > 0 && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Dados da Tabela AGR_USERS</h5>
                    <span className="badge bg-primary">{tableData.length} registros</span>
                  </div>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table className="table table-sm table-bordered">
                      <thead className="table-light sticky-top">
                        <tr>
                          {tableData[0] && Object.keys(tableData[0]).map((key) => (
                            <th key={key} style={{ fontSize: '0.85rem' }}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.map((row, idx) => (
                          <tr key={idx}>
                            {Object.values(row).map((val: any, i) => (
                              <td key={i} style={{ fontSize: '0.8rem' }}>{val}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Exibição dos dados da BAPI */}
              {bapiData && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Resultado BAPI_USER_GETLIST</h5>
                    <span className="badge bg-info">
                      {bapiData.USERLIST?.length || 0} usuários
                    </span>
                  </div>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {bapiData.USERLIST && bapiData.USERLIST.length > 0 ? (
                      <table className="table table-sm table-bordered">
                        <thead className="table-light sticky-top">
                          <tr>
                            {Object.keys(bapiData.USERLIST[0]).map((key) => (
                              <th key={key} style={{ fontSize: '0.85rem' }}>{key}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {bapiData.USERLIST.map((user: any, idx: number) => (
                            <tr key={idx}>
                              {Object.values(user).map((val: any, i) => (
                                <td key={i} style={{ fontSize: '0.8rem' }}>{val}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <pre className="bg-light p-3 rounded" style={{ fontSize: '0.8rem', maxHeight: '300px', overflow: 'auto' }}>
                        {JSON.stringify(bapiData, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="integration-modal-footer">
              <button className="btn btn-secondary" onClick={closeTestModal}>
                Fechar
              </button>
              {testResult?.success && (
                <>
                  <button 
                    className="btn btn-primary" 
                    onClick={readTable}
                    disabled={readingTable}
                  >
                    {readingTable ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Lendo...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="me-1">
                          <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm15 2h-4v3h4V4zm0 4h-4v3h4V8zm0 4h-4v3h3a1 1 0 0 0 1-1v-2zm-5 3v-3H6v3h4zm-5 0v-3H1v2a1 1 0 0 0 1 1h3zm-4-4h4V8H1v3zm0-4h4V4H1v3zm5-3v3h4V4H6zm4 4H6v3h4V8z"/>
                        </svg>
                        Ler Tabela AGR_USERS
                      </>
                    )}
                  </button>
                  <button 
                    className="btn btn-info" 
                    onClick={callBapi}
                    disabled={callingBapi}
                  >
                    {callingBapi ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Chamando...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="me-1">
                          <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
                          <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319z"/>
                        </svg>
                        Chamar BAPI_USER_GETLIST
                      </>
                    )}
                  </button>
                </>
              )}
              <button 
                className="btn btn-success" 
                onClick={testConnection}
                disabled={testing}
              >
                {testing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Testando...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="me-1">
                      <path d="M11.251.068a.5.5 0 0 1 .227.58L9.677 6.5H13a.5.5 0 0 1 .364.843l-8 8.5a.5.5 0 0 1-.842-.49L6.323 9.5H3a.5.5 0 0 1-.364-.843l8-8.5a.5.5 0 0 1 .615-.09z"/>
                    </svg>
                    Testar Conexão
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`integration-toast integration-toast-${toast.variant}`}>
          {toast.message}
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmState.visible && (
        <div className="integration-modal-overlay" onClick={hideConfirm}>
          <div className="integration-modal integration-modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="integration-modal-header">
              <h3>{confirmState.title}</h3>
              <button className="integration-modal-close" onClick={hideConfirm} aria-label="Fechar">
                ×
              </button>
            </div>
            <div className="integration-modal-body">
              <p>{confirmState.message}</p>
            </div>
            <div className="integration-modal-footer">
              <button className="btn btn-secondary" onClick={hideConfirm}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={() => { confirmState.onConfirm && confirmState.onConfirm(); hideConfirm(); }}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Integrations;
